/** @format */

import React from "react";
import { Box, Typography, alpha } from "@mui/material";
import { Link } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { colors } from "../../../utility/types/constant";

interface BusinessAuthShellProps {
    title: string;
    subtitle?: string;
    children: React.ReactNode;
    footer?: React.ReactNode;
}

// Collect-specific value props for the branding panel — deliberately different
// from the circle product's benefits so the two auth experiences feel related
// but distinct.
const COLLECT_BENEFITS = [
    "Recover overdue invoices on autopilot",
    "AI-drafted reminders that get you paid faster",
    "Two-way sync with QuickBooks Online",
    "Track promises, replies & recovery in one place",
];

const LOGO_SRC = "/images/how-it-works-images/primary-logo-1-1.png";

/** Small product badge used next to the FundsyFi wordmark. */
const CollectBadge: React.FC = () => (
    <Box
        component="span"
        sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1,
            py: 0.25,
            borderRadius: 1,
            bgcolor: alpha(colors.mainSecondary, 0.18),
            border: `1px solid ${alpha(colors.mainSecondary, 0.45)}`,
            color: colors.mainSecondary,
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 700,
            fontSize: "0.62rem",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
        }}
    >
        Collect
    </Box>
);

/**
 * Two-panel auth layout shared by every Collect business auth screen
 * (sign-up / team sign-up / verify / sign-in).
 *
 * Mirrors the circle auth design (dark branding panel + white form card,
 * decorative blobs, mint accent) but is visually distinct: a navy→blue
 * gradient backdrop, a "COLLECT" badge, and product-specific messaging.
 */
export const BusinessAuthShell: React.FC<BusinessAuthShellProps> = ({
    title,
    subtitle,
    children,
    footer,
}) => {
    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.blueRibbon} 100%)`,
                display: "flex",
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* Background decorative blobs */}
            <Box
                sx={{
                    position: "absolute",
                    top: -200,
                    right: -180,
                    width: 600,
                    height: 600,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${alpha(colors.mainSecondary, 0.12)} 0%, transparent 70%)`,
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    bottom: -160,
                    left: -160,
                    width: 520,
                    height: 520,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${alpha(colors.mainSecondary, 0.07)} 0%, transparent 70%)`,
                    pointerEvents: "none",
                }}
            />

            {/* ── Main layout ── */}
            <Box
                sx={{
                    display: "flex",
                    width: "100%",
                    maxWidth: 1100,
                    mx: "auto",
                    alignItems: "center",
                    px: { xs: 2, sm: 3, md: 5 },
                    py: { xs: 3, md: 0 },
                    minHeight: "100vh",
                }}
            >
                {/* ── Left branding panel (desktop only) ── */}
                <Box
                    sx={{
                        display: { xs: "none", lg: "flex" },
                        flexDirection: "column",
                        justifyContent: "center",
                        flex: "0 0 46%",
                        pr: 8,
                        py: 6,
                    }}
                >
                    {/* Logo + product badge */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mb: 5,
                        }}
                    >
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: 2,
                                overflow: "hidden",
                                boxShadow: `0 4px 14px ${alpha(colors.mainSecondary, 0.4)}`,
                            }}
                        >
                            <Box
                                component="img"
                                src={LOGO_SRC}
                                alt="FundsyFi Recover"
                                sx={{
                                    width: 42,
                                    height: 42,
                                    objectFit: "contain",
                                    display: "block",
                                }}
                            />
                        </Box>
                        <Typography
                            sx={{
                                color: "#fff",
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 800,
                                fontSize: "1.5rem",
                                letterSpacing: 0.3,
                            }}
                        >
                            FundsyFi
                        </Typography>
                        <CollectBadge />
                    </Box>

                    <Typography
                        sx={{
                            color: "#fff",
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 800,
                            fontSize: { lg: "2.2rem", xl: "2.6rem" },
                            lineHeight: 1.2,
                            mb: 2,
                        }}
                    >
                        Get paid,{" "}
                        <Box component="span" sx={{ color: colors.mainSecondary }}>
                            faster.
                        </Box>
                    </Typography>
                    <Typography
                        sx={{
                            color: alpha("#fff", 0.6),
                            mb: 4,
                            lineHeight: 1.7,
                            fontSize: "0.95rem",
                            maxWidth: 400,
                        }}
                    >
                        The accounts-receivable workspace that chases overdue
                        invoices for you so your team can focus on the work that
                        grows the business.
                    </Typography>

                    {COLLECT_BENEFITS.map((benefit, i) => (
                        <Box
                            key={i}
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1.5,
                                mb: 2,
                            }}
                        >
                            <CheckCircleIcon
                                sx={{
                                    color: colors.mainSecondary,
                                    fontSize: "1.1rem",
                                    mt: 0.3,
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                sx={{
                                    color: alpha("#fff", 0.78),
                                    fontSize: "0.88rem",
                                    lineHeight: 1.55,
                                }}
                            >
                                {benefit}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* ── Right form card ── */}
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        py: { xs: 0, lg: 4 },
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: 440,
                            bgcolor: "#fff",
                            borderRadius: { xs: 3, sm: 4 },
                            p: { xs: 3, sm: 4 },
                            boxShadow: "0 24px 80px rgba(0, 0, 0, 0.45)",
                            position: "relative",
                            zIndex: 1,
                        }}
                    >
                        {/* Mobile logo + badge */}
                        <Box
                            component={Link}
                            to="/business"
                            sx={{
                                display: { xs: "flex", lg: "none" },
                                alignItems: "center",
                                gap: 1,
                                mb: 2.5,
                                textDecoration: "none",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1.5,
                                    overflow: "hidden",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={LOGO_SRC}
                                    alt="FundsyFi Recover"
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        objectFit: "contain",
                                        display: "block",
                                    }}
                                />
                            </Box>
                            <Typography
                                sx={{
                                    color: colors.primary,
                                    fontFamily: "'Montserrat', sans-serif",
                                    fontWeight: 800,
                                    fontSize: "1.1rem",
                                }}
                            >
                                FundsyFi
                            </Typography>
                            <CollectBadge />
                        </Box>

                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 800,
                                color: colors.primary,
                                fontSize: "1.4rem",
                                mb: subtitle ? 0.4 : 2,
                            }}
                        >
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "text.secondary",
                                    mb: 2.5,
                                    lineHeight: 1.5,
                                }}
                            >
                                {subtitle}
                            </Typography>
                        )}

                        {children}

                        {footer && (
                            <Box sx={{ textAlign: "center", mt: 2.5 }}>
                                {footer}
                            </Box>
                        )}

                        <Typography
                            variant="caption"
                            sx={{
                                display: "block",
                                textAlign: "center",
                                mt: 2.5,
                                color: "text.disabled",
                            }}
                        >
                            © {new Date().getFullYear()} FundsyFi Recover
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
